import { type FC, useState } from "react";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import { RadioGroup } from "@/shared/components/ui/radio-group";
import { useDeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import { getEstateThreshold } from "@/shared/constants/estateThresholds";
import { formatCurrency } from "@/shared/utils/formatters";
import { WHY_WE_ASK_TITLE } from "@/shared/constants/whyWeAsk";

import { LegalAuthorityStepLayout } from "./LegalAuthorityStepLayout";
import { WhatCountsSection } from "./WhatCountsSection";

type AmountAnswer = "under" | "over" | "not-sure";

interface LegalAuthorityAmountStepProps {
  personFirstName: string;
  onBack: () => void;
  onContinue: (answer: AmountAnswer, threshold: number, state: string | null) => void;
}

export const LegalAuthorityAmountStep: FC<LegalAuthorityAmountStepProps> = ({
  personFirstName,
  onBack,
  onContinue,
}) => {
  const { state } = useDeceasedUserData();
  const threshold = getEstateThreshold(state);
  const formattedThreshold = formatCurrency(threshold);
  const personName = personFirstName || "your loved one";

  const [selected, setSelected] = useState<AmountAnswer | null>(null);
  const [showError, setShowError] = useState(false);

  const handleContinue = () => {
    if (!selected) {
      setShowError(true);
      return;
    }
    if (selected === "not-sure") {
      onContinue("under", threshold, state);
    } else {
      onContinue(selected, threshold, state);
    }
  };

  const handleSelect = (value: AmountAnswer) => {
    setSelected(value);
    setShowError(false);
  };

  const options: { value: AmountAnswer; label: string }[] = [
    { value: "under", label: `Under ${formattedThreshold}` },
    { value: "over", label: `Over ${formattedThreshold}` },
    { value: "not-sure", label: "I'm not sure" },
  ];

  return (
    <LegalAuthorityStepLayout
      title={`How much was in ${personName}'s name alone?`}
      titleTooltip={{
        title: WHY_WE_ASK_TITLE,
        content: `Each state lets estates under a dollar limit use a shorter process instead of full probate. A rough total tells us whether ${personName}'s estate may qualify, and a close guess is fine.`,
      }}
      description="Count anything that was only in his name. A close guess is fine, this can be updated later."
      activeStep={3}
      onBack={onBack}
      actionButton={
        <Button variant="brand" size="md" onClick={handleContinue}>
          Continue
        </Button>
      }
    >
      <RadioGroup
        value={selected ?? undefined}
        onValueChange={(v) => handleSelect(v as AmountAnswer)}
        className="flex flex-col gap-4"
      >
        {options.map(({ value, label }) => (
          <OptionCard
            key={value}
            label={label}
            selected={selected === value}
            onClick={() => handleSelect(value)}
            variant="radio"
            value={value}
          />
        ))}
      </RadioGroup>

      <FieldError message={showError ? "Select an option to continue" : undefined} />

      <WhatCountsSection personFirstName={personName} />
    </LegalAuthorityStepLayout>
  );
};
